import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'sonner';
import { Alert } from '@/components/alert';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import portalService from '@/services/portal/portalService';
import { usePortal } from '@/portal/PortalProvider';
import { PortalPasswordField } from './PortalPasswordField';
import { PASSWORD_HINT, confirmPasswordSchema, newPasswordSchema } from './passwordRules';

const schema = Yup.object().shape({
  current_password: Yup.string().required('Enter your current password.'),
  password: newPasswordSchema,
  password_confirmation: confirmPasswordSchema,
});

/** The guest's own account: who they are signed in as, and a password change. */
const PortalAccount = () => {
  const { guest, branding } = usePortal();

  const formik = useFormik({
    initialValues: { current_password: '', password: '', password_confirmation: '' },
    validationSchema: schema,
    onSubmit: async (values, { setStatus, resetForm }) => {
      setStatus(null);
      try {
        await portalService.changePassword(values.current_password, values.password, values.password_confirmation);
        resetForm();
        toast.success('Password changed.', { description: 'Any other devices you were signed in on have been signed out.' });
      } catch (error) {
        setStatus(getApiErrorMessage(error, 'The password could not be changed. Please try again.'));
      }
    },
  });

  return (
    <div className="grid gap-5 lg:grid-cols-2 lg:gap-7.5 items-start">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Your account</h3>
        </div>
        <div className="card-body flex flex-col gap-3 text-2sm">
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Name</span>
            <span className="text-gray-900 font-medium">{guest?.name}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Email</span>
            <span className="text-gray-900 font-medium">{guest?.email}</span>
          </div>
          <p className="text-2xs text-gray-500 pt-2 border-t border-gray-200">
            To change your name or email, contact {branding.firm_name || 'the firm'}.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Change password</h3>
        </div>
        <form className="card-body flex flex-col gap-4" onSubmit={formik.handleSubmit} noValidate>
          {formik.status && <Alert variant="danger">{formik.status}</Alert>}
          <PortalPasswordField
            id="portal-current-password"
            label="Current password"
            autoComplete="current-password"
            formik={formik}
            name="current_password"
          />
          <PortalPasswordField id="portal-change-password" label="New password" autoComplete="new-password" formik={formik} name="password" />
          <PortalPasswordField
            id="portal-change-confirm"
            label="Confirm new password"
            autoComplete="new-password"
            formik={formik}
            name="password_confirmation"
          />
          <p className="text-2xs text-gray-500 -mt-1">{PASSWORD_HINT}</p>
          <button type="submit" className="btn btn-dark w-fit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export { PortalAccount };
